import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { KeyEventArgs } from "../core/events.js";
import { clamp, type Point, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { paintGlyph } from "../themes/glyphRegistry.js";
import type { RadialWedgeVisual, Theme } from "../themes/Theme.js";

export interface RadialItem {
  label: string;
  /** A name from the glyph registry, drawn in the wedge. */
  glyph?: string;
  color?: Color | null;
  enabled?: boolean;
  data?: unknown;
}

export interface RadialMenuOptions extends ControlOptions {
  items?: readonly RadialItem[];
  /** Hole in the middle, as a fraction of the radius. */
  innerRadius?: number;
  /** Gap between wedges, in radians. */
  gap?: number;
  /** Where the first wedge starts, clockwise from 12 o'clock. */
  startAngle?: number;
  /** Ignore the pointer inside this fraction of the radius: no accidental picks. */
  deadzone?: number;
  font?: FontSpec | null;
  onSelect?: (item: RadialItem, index: number) => void;
}

const TAU = Math.PI * 2;
const DEFAULT_INNER = 0.38;
const DEFAULT_GAP = 0.02;
const DEFAULT_DEADZONE = 0.3;
const GLYPH_FRACTION = 0.18;

/**
 * The emote or ability wheel: a ring of choices picked by pointing.
 *
 * The best input shape in the whole set for a pad or a thumb, and the reason
 * is the deadzone in the middle. Nothing is selected while the stick is
 * centered, and pushing it any distance in a direction picks that wedge
 * outright — so a choice is one flick with no travel to a target, and letting
 * go without moving cancels. A menu of buttons cannot do that.
 *
 * Every wedge is one `rc.fillArc`, which is the only reason this is drawable
 * at all in a renderer with no rotation.
 */
export class RadialMenu extends Control {
  #items: RadialItem[];
  #highlighted = -1;

  innerRadius: number;
  gap: number;
  startAngle: number;
  deadzone: number;
  font: FontSpec | null;

  constructor(options: RadialMenuOptions = {}) {
    super({ width: 240, height: 240, tabStop: true, ...options });
    this.#items = [...(options.items ?? [])];
    this.innerRadius = clamp(options.innerRadius ?? DEFAULT_INNER, 0, 0.95);
    this.gap = Math.max(0, options.gap ?? DEFAULT_GAP);
    this.startAngle = options.startAngle ?? 0;
    this.deadzone = clamp(options.deadzone ?? DEFAULT_DEADZONE, 0, 0.95);
    this.font = options.font ?? null;
    if (options.onSelect) {
      this.on("itemactivate", (index) => {
        const item = this.#items[index];
        if (item) options.onSelect!(item, index);
      });
    }

    this.on("pointermove", (e) => this.#hover(e.x, e.y));
    this.on("pointerleave", () => this.#setHighlighted(-1));
    this.on("click", (e) => {
      this.#hover(e.x, e.y);
      this.activateHighlighted();
    });
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  get items(): readonly RadialItem[] {
    return this.#items;
  }
  set items(v: readonly RadialItem[]) {
    this.#items = [...v];
    this.#highlighted = -1;
    this.invalidate();
  }

  /** The wedge under the pointer, or -1. */
  get highlighted(): number {
    return this.#highlighted;
  }
  set highlighted(index: number) {
    this.#setHighlighted(index);
  }

  get center(): Point {
    return { x: this.width / 2, y: this.height / 2 };
  }

  get radius(): number {
    return Math.min(this.width, this.height) / 2;
  }

  /** How wide one wedge is, in radians. */
  get wedgeAngle(): number {
    return this.#items.length > 0 ? TAU / this.#items.length : 0;
  }

  /**
   * The wedge containing a local point, or -1.
   *
   * Inside the deadzone nothing is picked. That is the whole ergonomic: a
   * stick at rest, or a pointer near the center, means "not yet", and any
   * push at all means "that one".
   */
  indexAt(x: number, y: number): number {
    if (this.#items.length === 0) return -1;
    const c = this.center;
    const dx = x - c.x;
    const dy = y - c.y;
    const distance = Math.hypot(dx, dy);
    const outer = this.radius;
    if (outer <= 0 || distance < outer * this.deadzone || distance > outer) return -1;
    // Clockwise from 12 o'clock, matching fillArc's convention.
    let angle = Math.atan2(dx, -dy) - this.startAngle;
    angle = ((angle % TAU) + TAU) % TAU;
    return Math.min(this.#items.length - 1, Math.floor(angle / this.wedgeAngle));
  }

  /**
   * Pick by direction rather than by position — a stick's deflection, or a
   * d-pad. `(dx, dy)` need not be normalized; anything inside the deadzone
   * clears the highlight.
   */
  pointAt(dx: number, dy: number): number {
    const length = Math.hypot(dx, dy);
    if (length < this.deadzone) {
      this.#setHighlighted(-1);
      return -1;
    }
    const c = this.center;
    const reach = this.radius * (1 - this.deadzone) * 0.5 + this.radius * this.deadzone;
    const index = this.indexAt(c.x + (dx / length) * reach, c.y + (dy / length) * reach);
    this.#setHighlighted(index);
    return index;
  }

  /** Fire the highlighted wedge, if there is one and it is enabled. */
  activateHighlighted(): boolean {
    const item = this.#items[this.#highlighted];
    if (!item || item.enabled === false) return false;
    this.emit("itemactivate", this.#highlighted);
    return true;
  }

  #hover(x: number, y: number): void {
    this.#setHighlighted(this.indexAt(x, y));
  }

  #setHighlighted(index: number): void {
    if (index === this.#highlighted) return;
    this.#highlighted = index;
    this.emit("selectionchanged");
    this.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    const count = this.#items.length;
    if (count === 0) return;
    const step = (delta: number): void => {
      const from = this.#highlighted < 0 ? (delta > 0 ? -1 : 0) : this.#highlighted;
      this.#setHighlighted((((from + delta) % count) + count) % count);
      e.handled = true;
    };
    switch (e.key) {
      // Round the wheel one wedge at a time; the arrows are the fallback for
      // whoever is not driving this with a stick.
      case "ArrowRight":
      case "ArrowDown":
        step(1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        step(-1);
        break;
      case "Enter":
      case " ":
        if (this.activateHighlighted()) e.handled = true;
        break;
      default:
        break;
    }
  }

  /** Where a wedge's label sits, for a caller placing its own content. */
  labelPoint(index: number): Point {
    const c = this.center;
    const mid = this.startAngle + (index + 0.5) * this.wedgeAngle;
    const reach = this.radius * (1 + this.innerRadius) / 2;
    return { x: c.x + Math.sin(mid) * reach, y: c.y - Math.cos(mid) * reach };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const c = this.center;
    const outer = this.radius;
    if (outer <= 0 || this.#items.length === 0) return;
    const inner = outer * this.innerRadius;
    const wedge = this.wedgeAngle;
    const font = this.font ?? theme.metrics.font;

    this.#items.forEach((item, i) => {
      const from = this.startAngle + i * wedge + this.gap / 2;
      const to = from + wedge - this.gap;
      const disabled = item.enabled === false;
      const visual: RadialWedgeVisual = {
        ...this.visualState(),
        disabled,
        center: c,
        innerRadius: inner,
        outerRadius: outer,
        startAngle: from,
        endAngle: to,
        color: item.color ?? null,
        highlighted: i === this.#highlighted && !disabled,
      };
      theme.drawRadialWedge(rc, visual);

      const at = this.labelPoint(i);
      const ink = theme.radialWedgeTextColor(visual);

      if (item.glyph) {
        const size = outer * GLYPH_FRACTION;
        const box: Rect = { x: at.x - size / 2, y: at.y - size / 2 - 6, width: size, height: size };
        paintGlyph(item.glyph, { rc, box, ink, weight: Math.max(1, Math.round(size / 9)) });
      }

      const width = rc.measureText(item.label, font).width;
      rc.drawText(item.label, at.x - width / 2, at.y + (item.glyph ? outer * 0.1 : 0), font, ink, {
        baseline: "middle",
      });
    });

    // The label of whatever is picked, in the hole in the middle: at a glance
    // you read the center, not the wedge your thumb is covering.
    const chosen = this.#items[this.#highlighted];
    if (chosen && inner > 0) {
      const width = rc.measureText(chosen.label, theme.metrics.captionFont).width;
      rc.drawText(chosen.label, c.x - width / 2, c.y, theme.metrics.captionFont, p.text, {
        baseline: "middle",
      });
    }
  }

  /** A radial menu is a disc, not a square: corners fall through to what is behind. */
  protected override hitTestSelf(p: Point): boolean {
    const c = this.center;
    return Math.hypot(p.x - c.x, p.y - c.y) <= this.radius;
  }
}
